import React, { useState } from 'react';

export default function ContactForm({ title = "Send os en forespørgsel" }) {
  const [formData, setFormData] = useState({
    navn: '',
    telefon: '',
    email: '',
    beskrivelse: '',
    website_url: '' // Honeypot field - must stay empty
  });

  const [status, setStatus] = useState({
    submitting: false,
    submitted: false,
    error: null
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check honeypot
    if (formData.website_url) {
      // Spam bot detected, fake success silently
      setStatus({ submitting: false, submitted: true, error: null });
      return;
    }

    if (!formData.navn || !formData.telefon || !formData.email) {
      setStatus({ submitting: false, submitted: false, error: 'Udfyld venligst alle påkrævede felter.' });
      return;
    }

    setStatus({ submitting: true, submitted: false, error: null });

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          navn: formData.navn,
          telefon: formData.telefon,
          email: formData.email,
          postnr: formData.postnr,
          beskrivelse: formData.beskrivelse,
          website_url: formData.website_url
        })
      });

      if (response.ok) {
        setStatus({ submitting: false, submitted: true, error: null });
        setFormData({ navn: '', telefon: '', email: '', postnr: '', beskrivelse: '', website_url: '' });
      } else {
        const data = await response.json().catch(() => ({}));
        setStatus({
          submitting: false,
          submitted: false,
          error: data.message || 'Der opstod en fejl ved afsendelse. Prøv venligst igen eller ring direkte.'
        });
      }
    } catch (err) {
      setStatus({
        submitting: false,
        submitted: false,
        error: 'Netværksfejl. Kontrollér din forbindelse og prøv igen.'
      });
    }
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-none p-6 sm:p-8 shadow-xl border-t-4 border-t-[#F4AF00]">
      <h3 className="text-xl sm:text-2xl font-black text-[#121212] mb-2 uppercase tracking-tight">
        {title}
      </h3>
      <p className="text-slate-600 text-sm mb-6">
        Udfyld formularen, så vender vi hurtigt tilbage med et uforpligtende tilbud eller rådgivning.
      </p>

      {status.submitted ? (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 text-center animate-fade-in">
          <div className="w-12 h-12 bg-emerald-100 text-emerald-700 rounded-full flex items-center justify-center mx-auto mb-3 text-xl font-bold">
            <i className="fa-solid fa-check" aria-hidden="true" />
          </div>
          <h4 className="text-lg font-bold text-slate-900 mb-1">Tak for din forespørgsel!</h4>
          <p className="text-slate-700 text-sm">
            Vi har modtaget din besked og kontakter dig hurtigst muligt på e-mail eller telefon.
          </p>
          <button
            onClick={() => setStatus({ submitting: false, submitted: false, error: null })}
            className="mt-4 text-xs text-amber-700 hover:text-amber-800 underline font-semibold"
          >
            Send en ny forespørgsel
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          {/* Honeypot antispam field */}
          <input
            type="text"
            name="website_url"
            value={formData.website_url}
            onChange={handleChange}
            className="sr-only absolute -left-[9999px]"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
          />

          {status.error && (
            <div className="p-3.5 rounded-lg bg-rose-50 border border-rose-200 text-rose-800 text-xs flex items-center gap-2 font-medium">
              <i className="fa-solid fa-circle-exclamation text-rose-600 text-sm flex-shrink-0" aria-hidden="true" />
              <span>{status.error}</span>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="navn" className="block text-xs font-bold text-zinc-700 uppercase tracking-wider mb-1">
                Navn <span className="text-[#F4AF00]">*</span>
              </label>
              <input
                type="text"
                id="navn"
                name="navn"
                required
                value={formData.navn}
                onChange={handleChange}
                placeholder="Dit fulde navn"
                className="w-full px-4 py-2.5 rounded-none bg-zinc-50 border border-zinc-300 text-zinc-900 placeholder-zinc-400 focus:outline-none focus:bg-white focus:border-[#F4AF00] focus:ring-2 focus:ring-[#F4AF00]/20 transition-colors text-sm font-medium"
              />
            </div>

            <div>
              <label htmlFor="postnr" className="block text-xs font-bold text-zinc-700 uppercase tracking-wider mb-1">
                Postnr.
              </label>
              <input
                type="text"
                id="postnr"
                name="postnr"
                value={formData.postnr}
                onChange={handleChange}
                placeholder="f.eks. 9700"
                className="w-full px-4 py-2.5 rounded-none bg-zinc-50 border border-zinc-300 text-zinc-900 placeholder-zinc-400 focus:outline-none focus:bg-white focus:border-[#F4AF00] focus:ring-2 focus:ring-[#F4AF00]/20 transition-colors text-sm font-medium"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="telefon" className="block text-xs font-bold text-zinc-700 uppercase tracking-wider mb-1">
                Telefonnummer <span className="text-[#F4AF00]">*</span>
              </label>
              <input
                type="tel"
                id="telefon"
                name="telefon"
                required
                value={formData.telefon}
                onChange={handleChange}
                placeholder="f.eks. 25623505"
                className="w-full px-4 py-2.5 rounded-none bg-zinc-50 border border-zinc-300 text-zinc-900 placeholder-zinc-400 focus:outline-none focus:bg-white focus:border-[#F4AF00] focus:ring-2 focus:ring-[#F4AF00]/20 transition-colors text-sm font-medium"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-xs font-bold text-zinc-700 uppercase tracking-wider mb-1">
                E-mailadresse <span className="text-[#F4AF00]">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                required
                value={formData.email}
                onChange={handleChange}
                placeholder="din@email.dk"
                className="w-full px-4 py-2.5 rounded-none bg-zinc-50 border border-zinc-300 text-zinc-900 placeholder-zinc-400 focus:outline-none focus:bg-white focus:border-[#F4AF00] focus:ring-2 focus:ring-[#F4AF00]/20 transition-colors text-sm font-medium"
              />
            </div>
          </div>

          <div>
            <label htmlFor="beskrivelse" className="block text-xs font-bold text-zinc-700 uppercase tracking-wider mb-1">
              Beskrivelse af opgave
            </label>
            <textarea
              id="beskrivelse"
              name="beskrivelse"
              rows={4}
              value={formData.beskrivelse}
              onChange={handleChange}
              placeholder="Beskriv opgaven du har brug for hjælp til..."
              className="w-full px-4 py-2.5 rounded-none bg-zinc-50 border border-zinc-300 text-zinc-900 placeholder-zinc-400 focus:outline-none focus:bg-white focus:border-[#F4AF00] focus:ring-2 focus:ring-[#F4AF00]/20 transition-colors text-sm font-medium resize-y"
            />
          </div>

          <button
            type="submit"
            disabled={status.submitting}
            className="w-full py-3.5 px-6 bg-[#F4AF00] hover:bg-[#D99B00] text-[#121212] font-black text-sm tracking-wide uppercase shadow-md transition-all flex items-center justify-center gap-2 focus:outline-none focus:ring-2 focus:ring-[#F4AF00] disabled:opacity-50 cursor-pointer"
          >
            {status.submitting ? (
              <>
                <i className="fa-solid fa-spinner animate-spin" aria-hidden="true" />
                <span>Sender forespørgsel...</span>
              </>
            ) : (
              <>
                <span>Send forespørgsel</span>
                <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
              </>
            )}
          </button>
        </form>
      )}
    </div>
  );
}
